import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { Refresh, UploadFile } from '@mui/icons-material';
import { InkyWorking } from '../brand';
import { useAIRequests } from '../../context/AIRequestContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import {
  IMPORT_STEPS,
  PRESENTATION_ACCEPT,
  PresentationImportError,
  retryPresentationImport,
} from '../../services/presentationUploadService';
import usePresentationImportStatus from './usePresentationImportStatus';

/**
 * Index of the active step for a stage (IMPORT_STEPS order; 'ready' completes every step).
 * @param {string} stage - deriveImportStage stage.
 * @returns {number}
 */
export function activeStepFor(stage) {
  switch (stage) {
    case 'uploading':
      return 0;
    case 'uploaded':
    case 'reading':
      return 1;
    case 'suggesting':
      return 2;
    case 'ready':
      return IMPORT_STEPS.length;
    default:
      return -1;
  }
}

/**
 * Live progress of a PowerPoint import, shown in the editor while the template is 'importing':
 * Uploaded, Reading slides, Suggesting prompts, Ready; or Failed with Retry / Choose file.
 *
 * @param {object} props - Props.
 * @param {string} props.templateId - Template being imported.
 * @param {(template: object) => void} [props.onReady] - Called once when the import is ready.
 * @param {(stage: string) => void} [props.onStageChange] - Called with each new stage once the rows have loaded.
 * @param {() => void} [props.onBack] - "Back to templates".
 * @param {object} [props.statusOptions] - Options for usePresentationImportStatus (tests).
 * @param {object} [props.retryOptions] - Extra options for retryPresentationImport (tests: client, invoke).
 * @returns {React.ReactElement}
 */
export default function PresentationImportProgress({ templateId, onReady, onStageChange, onBack, statusOptions, retryOptions }) {
  const status = usePresentationImportStatus(templateId, statusOptions);
  const { createRequest, refreshRequests } = useAIRequests();
  const { state: authState } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const fileInputRef = useRef(null);
  const readyFiredRef = useRef(false);

  const { stage, message, error, needsFile, template, removed, code } = status;

  useEffect(() => {
    if (stage !== 'ready' || readyFiredRef.current || !status.loaded) return;
    readyFiredRef.current = true;
    if (typeof onReady === 'function') onReady(template);
  }, [stage, status.loaded, template, onReady]);

  useEffect(() => {
    if (status.loaded && typeof onStageChange === 'function') onStageChange(stage);
  }, [stage, status.loaded, onStageChange]);

  /**
   * Re-run the import (stored master, or `file` when given).
   * @param {File} [file] - Newly chosen file.
   * @returns {Promise<void>}
   */
  const retry = async (file) => {
    const accountId = authState?.activeAccount?.id;
    if (!template || !accountId) return;
    setRetrying(true);
    setRetryError('');
    try {
      const result = await retryPresentationImport({ template, accountId, file, createRequest, ...(retryOptions || {}) });
      if (result.error) setRetryError(result.error.message);
      if (typeof refreshRequests === 'function') refreshRequests();
    } catch (err) {
      if (err instanceof PresentationImportError && err.code === 'NEEDS_FILE') {
        fileInputRef.current?.click();
      } else {
        setRetryError(err?.message || 'Could not retry the import.');
      }
    } finally {
      setRetrying(false);
      await status.refresh();
    }
  };

  const onFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) retry(file);
  };

  const activeStep = activeStepFor(stage);
  const failed = stage === 'failed';
  const name = template?.name || 'PowerPoint template';
  const overLimit = code === 'TEMPLATE_LIMIT';
  let intro;
  if (failed && removed) {
    intro = overLimit
      ? 'Nothing was imported and no template was kept, so it does not count toward your plan.'
      : 'There is nothing left to retry here.';
  } else if (failed) {
    intro = 'Your original file is kept. Fix the problem below and retry.';
  } else if (stage === 'uploading') {
    intro = 'Keep this tab open until the upload finishes. After that you can leave this page: the import keeps running and the template appears in your template list when it is ready.';
  } else {
    intro = 'You can leave this page. The import keeps running, and the template appears in your template list when it is ready.';
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto', my: 3 }} data-testid="presentation-import-progress">
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600, mb: 0.5, overflowWrap: 'anywhere' }}>
        {failed ? (overLimit ? 'Template limit reached' : 'Import failed') : stage === 'ready' ? 'Import finished' : `Importing ${name}`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {intro}
      </Typography>

      <Stepper activeStep={failed ? -1 : activeStep} alternativeLabel sx={{ mb: 2 }}>
        {IMPORT_STEPS.map((step, index) => (
          <Step key={step.key} completed={!failed && activeStep > index}>
            <StepLabel>{index === 0 && stage === 'uploading' ? 'Uploading' : step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {!failed && stage !== 'ready' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} role="status" aria-live="polite">
          <InkyWorking label={message.replace(/\.\.\.$/, '')} mood="writing" />
          <LinearProgress
            variant={status.request?.progress ? 'determinate' : 'indeterminate'}
            value={Math.max(3, Math.min(100, Number(status.request?.progress) || 0))}
            aria-label="Import progress"
          />
        </Box>
      )}

      {failed && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {error}
        </Alert>
      )}
      {retryError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="presentation-import-retry-error">
          {retryError}
        </Alert>
      )}
      {status.loadError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {status.loadError}
        </Alert>
      )}

      {failed && removed && typeof onBack === 'function' && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={onBack}>
            Back to templates
          </Button>
        </Box>
      )}
      {failed && !removed && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!needsFile && (
            <Button variant="contained" startIcon={<Refresh />} onClick={() => retry()} disabled={retrying}>
              {retrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}
          <Button
            variant={needsFile ? 'contained' : 'outlined'}
            startIcon={<UploadFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={retrying}
          >
            Choose file again
          </Button>
          {typeof onBack === 'function' && (
            <Button onClick={onBack} disabled={retrying}>
              Back to templates
            </Button>
          )}
        </Box>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={PRESENTATION_ACCEPT}
        hidden
        onChange={onFileChosen}
        data-testid="presentation-import-retry-input"
      />
    </Paper>
  );
}
